<script setup lang="ts">
import { twMerge } from 'tailwind-merge'
import { useAppStore } from '~/stores/app'

const router = useRouter()

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    variant?: 'primary' | 'secondary' | 'tertiary' | 'text' | 'unstyled'
    href: string
    customNavigation?: () => void
    as?: string
  }>(),
  {
    variant: 'primary',
    as: 'a',
    customNavigation: null,
  },
)

const app = useAppStore()

function navigate() {
  if (props.customNavigation) {
    props.customNavigation()
  } else if (app.isDifferentAppVersion || props.href.startsWith('http')) {
    window.location.assign(props.href)
  } else {
    router.push(props.href)
  }
}

const isUnstyled = computed(() => props.variant === 'unstyled')
</script>

<!--
  We want this link's tag element to be customizable,
  in case we want to put a clickable element inside
  another one. Nesting anchors inside each other, or
  buttons inside buttons is invalid HTML, and
  therefore it causes hydration mismatch errors.
-->
<template>
  <component
    :is="as"
    v-bind="$attrs"
    class="cursor-pointer"
    :class="
      isUnstyled
        ? ''
        : twMerge(
            `btn ${variant}-btn flex items-center justify-center gap-x-3 rounded px-4 py-2 text-xs font-semibold text-s-900`,
            $attrs.class as string,
          )
    "
    :href="href"
    :role="as !== 'a' && as !== 'area' ? 'link' : undefined"
    @keypress.enter="navigate"
    @click.stop.prevent="navigate"
  >
    <slot />
  </component>
</template>
